package com.karyadina.service

import com.karyadina.model.*
import kotlinx.browser.document
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.Image
import kotlin.coroutines.resume
import kotlin.coroutines.suspendCoroutine

actual class ImageToPdfServiceImpl : ImageToPdfService {
    
    actual override suspend fun convertImagesToPdf(
        images: List<ImageItem>,
        config: PdfConfig,
        outputFileName: String
    ): PdfConversionResult {
        return try {
            if (js("typeof window.jspdf === 'undefined'") as Boolean) {
                return PdfConversionResult(false, error = "PDF library not loaded. Please refresh the page.")
            }

            console.log("Starting PDF generation with ${images.size} images")
            
            // Fix: Extract values in Kotlin to avoid name mangling issues in JS block
            // Map "custom" to "a4" for now as jsPDF doesn't support "custom" string
            var pageSize = config.pageSize.lowercase()
            if (pageSize == "custom") {
                pageSize = "a4"
            }
            val orientation = config.orientation.lowercase()
            
            val options = kotlin.js.json(
                "format" to pageSize,
                "orientation" to orientation
            )
            
            val doc = js("new window.jspdf.jsPDF(options)")
            var firstPage = true

            images.forEachIndexed { index, imageItem ->
                console.log("Processing image $index: ${imageItem.name}")
                // Load image
                val imgElement = Image()
                val loaded = suspendCoroutine<Boolean> { cont ->
                    imgElement.onload = { 
                        console.log("Image $index loaded")
                        cont.resume(true) 
                    }
                    imgElement.onerror = { _, _, _, _, _ -> 
                        console.error("Failed to load image $index")
                        cont.resume(false) 
                    }
                    imgElement.src = imageItem.uri
                }

                if (loaded) {
                    if (!firstPage) {
                        try {
                            doc.addPage(config.pageSize.lowercase(), config.orientation.lowercase())
                            console.log("Added new page for image $index")
                        } catch (e: Exception) {
                            console.error("Failed to add page", e)
                        }
                    }
                    firstPage = false

                    val pageWidth = doc.internal.pageSize.getWidth() as Double
                    val pageHeight = doc.internal.pageSize.getHeight() as Double
                    
                    val margin = config.margin.toDouble()
                    
                    val availableWidth = pageWidth - (margin * 2)
                    val availableHeight = pageHeight - (margin * 2)
                    
                    // Calculate scaling to fit within available area
                    val imgRatio = imgElement.width.toDouble() / imgElement.height.toDouble()
                    
                    var drawWidth = availableWidth
                    var drawHeight = availableWidth / imgRatio
                    
                    if (drawHeight > availableHeight) {
                        drawHeight = availableHeight
                        drawWidth = availableHeight * imgRatio
                    }
                    
                    // Center content
                    val x = margin + (availableWidth - drawWidth) / 2
                    val y = margin + (availableHeight - drawHeight) / 2

                    // We need to draw to canvas to ensure we have data compatible with addImage
                    val canvas = document.createElement("canvas") as HTMLCanvasElement
                    canvas.width = imgElement.width
                    canvas.height = imgElement.height
                    val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
                    ctx.drawImage(imgElement, 0.0, 0.0)
                    
                    val quality = config.quality / 100.0
                    val imgData = canvas.toDataURL("image/jpeg", quality)
                    
                    try {
                        doc.addImage(imgData, "JPEG", x, y, drawWidth, drawHeight)
                        console.log("Added image $index to PDF")
                    } catch (e: Exception) {
                        console.error("Failed to add image to PDF", e)
                    }
                } else {
                    console.warn("Skipping image $index due to load failure")
                }
            }

            console.log("Generating PDF Blob...")
            // internal.getNumberOfPages() might be available as a method or property depending on version
            // In safe kotlin wrapper: (doc.internal as Any).getNumberOfPages() ? 
            // Let's rely on our counting or standard API.
            // doc.getNumberOfPages() exists in some versions.
            // Let's try standard doc.internal.getNumberOfPages() via dynamic
            val pageCount = try {
                doc.internal.getNumberOfPages() as Int
            } catch (e: Exception) {
                0
            }

            val blob = doc.output("blob")
            val blobUrl = js("URL.createObjectURL(blob)") as String
            // Fix: blob.size is a JS number, cast to Double then toLong
            val size = (blob.size as Double).toLong()
            
            console.log("PDF generated, size: $size bytes, pages: $pageCount")

            PdfConversionResult(
                success = true,
                filePath = blobUrl,
                fileName = outputFileName,
                sizeInBytes = size,
                pageCount = pageCount
            )
        } catch (e: Exception) {
            console.error("PDF generation failed", e)
            PdfConversionResult(false, error = "Failed to generate PDF: ${e.message}")
        }
    }
    
    actual override fun estimatePdfSize(images: List<ImageItem>, config: PdfConfig): Long {
        val totalImageSize = images.sumOf { it.sizeInBytes }
        val qualityFactor = config.quality / 100.0
        val pdfOverhead = 50000L
        return (totalImageSize * qualityFactor).toLong() + pdfOverhead
    }
    
    actual override fun validateImages(images: List<ImageItem>): ValidationResult {
        val errors = mutableListOf<String>()
        
        if (images.isEmpty()) {
            errors.add("No images selected")
        }
        
        if (images.size > 50) {
            errors.add("Too many images (max 50)")
        }
        
        images.forEach { image ->
            if (image.sizeInMB > 10) {
                errors.add("Image ${image.name} is too large (max 10MB)")
            }
        }
        
        return ValidationResult(
            isValid = errors.isEmpty(),
            errors = errors
        )
    }
}
